import React, { useState, useEffect } from 'react';
import { makeStyles } from '@mui/styles';
import { Box, Grid, Pagination } from '@mui/material';
import classNames from 'classnames';
import AdminMemberCard from '../Search Results/AdminMemberCard';
import GroupMemberEmpty from './GroupMemberEmpty';
import GroupMemberLoading from './GroupMemberLoading';
import { GroupMemberHookReturn } from '../../hooks/useGroupMemberHook';

const useStyles = makeStyles(() => ({
    container: {
        width: '100%',
    },
    filterContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        marginBottom: '20px',
        flexWrap: 'wrap'
    },
    filterLabel: {
        fontSize: '16px',
        fontWeight: 600,
        color: '#1F2152',
        fontFamily: 'Manrope',
        marginRight: '8px'
    },
    filterButton: {
        padding: '4px 12px',
        borderRadius: '4px',
        border: '0.5px solid #A6A6BF',
        background: 'white',
        color: '#A6A6BF',
        fontSize: '0.8rem',
        fontWeight: 700,
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        fontFamily: 'Manrope',
        textTransform: 'uppercase',
        '&:hover': {
            background: '#E5E7EB'
        }
    },
    activeFilter: {
        border: 'none',
        background: '#7378E8',
        color: '#FFFFFF',
        '&:hover': {
            background: '#6366F1'
        }
    },
    resultsContainer: {
        width: '100%'
    },
    paginationContainer: {
        display: 'flex',
        justifyContent: 'center',
        marginTop: '32px',
        marginBottom: '8px',
    }
}));

type FilterType = 'ALL MEMBERS' | 'LIVE' | 'PENDING' | 'UNENROLLED' | 'ELIGIBLE/TARGET';

interface GroupMemberListProps {
    groupId: number;
    isVisible: boolean;
    hook: GroupMemberHookReturn;
}

const GroupMemberList: React.FC<GroupMemberListProps> = ({
    groupId,
    isVisible,
    hook
}) => {
    const classes = useStyles();
    const [activeFilter, setActiveFilter] = useState<FilterType>('ALL MEMBERS');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(12); // You can make this configurable if needed
    
    // Get the member state for this specific group
    const groupMemberState = hook.getGroupMemberState(groupId);
    const { members, error: membersError, isLoading: isMembersLoading } = groupMemberState;

    const filters: FilterType[] = ['ALL MEMBERS', 'LIVE', 'PENDING', 'ELIGIBLE/TARGET', 'UNENROLLED'];

    // Reset to page 1 when filter changes
    useEffect(() => {
        setCurrentPage(1);
    }, [activeFilter]);

    const getFilteredMembers = () => {
        if (!members) return [];
        if (activeFilter === 'ALL MEMBERS') return members;
        switch (activeFilter) {
            case 'LIVE':
                return members.filter(member => member.profile_stage === 'LIVE' || member.profile_stage === 'NEEDS_REVIEW');
            case 'PENDING':
                return members.filter(member => member.profile_stage === 'PENDING');
            case 'UNENROLLED':
                return members.filter(member => member.profile_stage === 'EXITED' || member.profile_stage === 'UNSUCCESSFUL_PENDING' || member.profile_stage === 'NO_LONGER_ELIGIBLE' || member.profile_stage === 'TARGET_NOT_INTERESTED' || member.profile_stage === 'NEVER_ENGAGED');
            case 'ELIGIBLE/TARGET':
                return members.filter(member => member.profile_stage === 'ELIGIBILITY_LIST' || member.profile_stage === 'TARGET_LIST');
            default:
                return members;
        }
    };

    const getPaginatedMembers = () => {
        const filteredMembers = getFilteredMembers();
        const startIndex = (currentPage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        return filteredMembers.slice(startIndex, endIndex);
    };

    const getPaginationInfo = () => {
        const filteredMembers = getFilteredMembers();
        const totalItems = filteredMembers.length;
        const totalPages = Math.ceil(totalItems / itemsPerPage);
        
        return { totalItems, totalPages };
    };

    const handlePageChange = (_event: React.ChangeEvent<unknown>, page: number) => {
        setCurrentPage(page);
    };

    if (!isVisible) return null;

    return (
        <div className={classes.container}>
            {/* Filter Buttons */}
            <div className={classes.filterContainer}>
                <span className={classes.filterLabel}>Filter by:</span>
                {filters.map((filter) => (
                    <button
                        key={filter}
                        className={classNames(
                            classes.filterButton,
                            activeFilter === filter && classes.activeFilter
                        )}
                        onClick={() => setActiveFilter(filter)}
                    >
                        {filter}
                    </button>
                ))}
            </div>

            {/* Results */}
            <div className={classes.resultsContainer}>
                {isMembersLoading ? (
                    <GroupMemberLoading />
                ) : getFilteredMembers().length === 0 || membersError ? (
                    <GroupMemberEmpty />
                ) : (
                    <>
                        {/* Member Grid */}
                        <Box sx={{ width: '100%' }}>
                            <Grid container spacing={2}>
                                {getPaginatedMembers().map((member) => (
                                    <Grid item xs={12} sm={6} md={4} key={`member-${member.id}`} sx={{ height: 175 }}>
                                        <AdminMemberCard member={member} />
                                    </Grid>
                                ))}
                            </Grid>
                        </Box>

                        {/* Pagination Controls */}
                        {getPaginationInfo().totalPages > 1 && (
                            <div className={classes.paginationContainer}>
                                <Pagination
                                    count={getPaginationInfo().totalPages}
                                    page={currentPage}
                                    onChange={handlePageChange}
                                    sx={{
                                        '& .MuiPaginationItem-root': {
                                            color: '#1F2152',
                                            '&:hover': {
                                                backgroundColor: '#E5E7EB'
                                            },
                                            '&.Mui-selected': {
                                                backgroundColor: '#7378E8',
                                                color: '#FFFFFF'
                                            }
                                        }
                                    }}
                                    size="medium"
                                    showFirstButton={false}
                                    showLastButton={false}
                                />
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default GroupMemberList; 